import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ReportComponentProps } from '../reportDefinitions';
import { fetchDescendingOrderReport, type ProgramReportRow } from '../reportsApi';

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const deriveMultiTierLabel = (row: ProgramReportRow): string => {
  if (row.hasTieredPrizes === true || row.multiPrizeIndicator) return 'Yes';
  if (row.hasTieredPrizes === false) return 'No';
  return row.multiPrizeIndicator ? 'Yes' : '—';
};

const safelyGetDomain = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const DescendingOrderReport: React.FC<ReportComponentProps> = () => {
  const query = useQuery({
    queryKey: ['reports', 'descending-order'],
    queryFn: fetchDescendingOrderReport,
    staleTime: 5 * 60 * 1000
  });

  const rows = React.useMemo(() => query.data ?? [], [query.data]);

  return (
    <div className="report-root">
      <style>{`
        .report-root {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .report-header {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .report-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        .report-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          align-self: center;
        }
        .report-brand img {
          height: 120px;
          width: auto;
        }
        .report-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex: 1;
        }
        .report-header-spacer {
          flex: 1;
        }
        .report-actions button {
          padding: 8px 14px;
          border-radius: 6px;
          border: 1px solid var(--color-border-light, #cbd5f5);
          background: var(--color-surface-muted, #f8fafc);
          color: var(--color-text-primary, #0f172a);
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .report-actions button:hover {
          background: var(--color-surface-subtle, #eef2ff);
          transform: translateY(-1px);
        }
        .report-actions button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .report-print-btn {
          background: #fde047 !important;
          color: #111827 !important;
          border: 1px solid #111827 !important;
          border-radius: 6px !important;
          padding: 8px 16px !important;
          font-weight: 700 !important;
          box-shadow: none !important;
        }
        .report-print-btn:hover {
          background: #facc15 !important;
        }
        .report-table-shell {
          border: 1px solid var(--color-border-light, #e2e8f0);
          border-radius: 12px;
          overflow: hidden;
          overflow-x: auto;
          background: var(--color-surface, #ffffff);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
        }
        table.report-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.88rem;
          background: transparent;
        }
        table.report-table thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: var(--color-surface-muted, #f1f5f9);
          text-align: left;
          padding: 12px;
          font-weight: 600;
          color: var(--color-text-muted, #475569);
          border-bottom: 1px solid var(--color-border-light, #e2e8f0);
          border-right: 1px solid var(--color-border-ghost, #f3f4f6);
        }
        table.report-table tbody td {
          padding: 12px;
          border-bottom: 1px solid var(--color-border-light, #e2e8f0);
          border-right: 1px solid var(--color-border-ghost, #f8fafc);
        }
        table.report-table tbody tr:nth-child(even) {
          background: var(--color-surface-muted, #f8fafc);
        }
        table.report-table tbody tr:hover {
          background: var(--color-surface-subtle, #eef2ff);
        }
        .tag-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .tag-sql {
          background: rgba(12, 74, 110, 0.12);
          color: #0c4a6e;
        }
        .tag-hybrid1 {
          background: rgba(49, 196, 141, 0.15);
          color: #0f5132;
        }
        .tag-hybrid2 {
          background: rgba(132, 112, 255, 0.18);
          color: #4338ca;
        }
        .prio-chip {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 12px;
          background: rgba(56, 189, 248, 0.18);
          color: #0369a1;
          font-weight: 600;
          font-size: 12px;
        }
        .audit-indicator {
          display: inline-block;
          margin-top: 6px;
          padding: 2px 6px;
          border-radius: 6px;
          background: rgba(234, 179, 8, 0.18);
          color: #92400e;
          font-size: 11px;
          font-weight: 600;
        }
        .notes-placeholder {
          min-height: 40px;
        }
        .notes-header {
          white-space: nowrap;
        }
        .link-cell a {
          color: #2563eb;
          text-decoration: none;
          display: inline-block;
          line-height: 1.3;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .link-cell a:hover {
          text-decoration: underline;
        }
        .website-header {
          width: 180px;
          min-width: 160px;
        }
        .website-cell {
          max-width: 200px;
          white-space: normal;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
        .muted {
          color: #64748b;
        }
        @media print {
          .report-root { gap: 12px; }
          .report-header { flex-direction: column; }
          .report-actions { display: none; }
          body { background: #ffffff; }
          .report-brand img { height: 72px; }
          .notes-column { display: table-cell !important; }
          .notes-placeholder { min-height: 80px; }
          table.report-table tbody tr:nth-child(even) { background: #ffffff; }
        }
        @media screen {
          .notes-column { display: none; }
        }
      `}</style>

      <div className="report-header">
        <div className="report-brand">
          <img src="/BAM-logo-embedded.svg" alt="Book Award Mastery" />
        </div>

        <div className="report-header-row">
          <div className="report-header-spacer" />
          <div className="report-actions">
            <button type="button" onClick={() => query.refetch()} disabled={query.isLoading}>
              {query.isLoading ? 'Refreshing…' : 'Refresh data'}
            </button>
            <button type="button" className="report-print-btn" onClick={() => window.print()}>
              Print report
            </button>
          </div>
        </div>
      </div>

      {query.isError && (
        <div style={{
          padding: '12px 14px',
          borderRadius: 10,
          border: '1px solid rgba(220, 38, 38, 0.2)',
          background: 'rgba(254, 226, 226, 0.8)',
          color: '#991b1b',
          marginBottom: 16
        }}>
          Failed to load descending order list. {(query.error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      {query.isLoading ? (
        <div style={{ padding: '24px 12px', color: '#475569' }}>Loading live report…</div>
      ) : (
        <>
          <div className="report-table-shell">
            <table className="report-table" data-testid="descending-order-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Program</th>
                <th>BACI</th>
                <th>Organization</th>
                <th className="website-header">Website</th>
                <th>Categories</th>
                <th>PrioList</th>
                <th>Search</th>
                <th>Multi-tier</th>
                <th>Total prizes*</th>
                <th>Original names</th>
                <th>Vetting status</th>
                <th className="notes-header notes-column">Personal notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const totalPrizes = row.calculatedWinOpportunities ?? row.totalWinOpportunities;
                const missingOriginal = row.categoriesMissingOriginal > 0;
                return (
                  <tr key={row.programId}>
                    <td className="muted">{index + 1}</td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{row.programName}</div>
                    </td>
                    <td>{row.baciProgramCode ?? '—'}</td>
                    <td>{row.programOrganization ?? '—'}</td>
                    <td className="link-cell website-cell">
                      {row.websiteUrl ? (
                        <a href={row.websiteUrl} target="_blank" rel="noopener noreferrer">
                          {safelyGetDomain(row.websiteUrl)}
                        </a>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <div>{formatNumber(row.categoryCount)}</div>
                      {missingOriginal ? (
                        <span className="audit-indicator">Missing {formatNumber(row.categoriesMissingOriginal)}</span>
                      ) : null}
                    </td>
                    <td>
                      {row.priorityList ? <span className="prio-chip">Yes</span> : <span className="muted">No</span>}
                    </td>
                    <td>
                      {row.searchMode === 'Hybrid2' && <span className="tag-pill tag-hybrid2">Hybrid2</span>}
                      {row.searchMode === 'Hybrid1' && <span className="tag-pill tag-hybrid1">Hybrid1</span>}
                      {row.searchMode === 'SQL' && <span className="tag-pill tag-sql">SQL</span>}
                    </td>
                    <td>{deriveMultiTierLabel(row)}</td>
                    <td>{formatNumber(totalPrizes)}</td>
                    <td className="name-metadata">
                      <span><span className="label">Original:</span> {formatNumber(row.categoriesWithOriginal)}</span>
                      <span><span className="label">Display:</span> {formatNumber(row.categoriesWithDisplay)}</span>
                    </td>
                    <td>{row.vettingStatus ?? '—'}</td>
                    <td className="notes-column notes-placeholder" />
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
            * Estimated total prizes multiplies <em>prizes per category</em> by total categories when an explicit total is unavailable.
          </div>
        </>
      )}
    </div>
  );
};

export default DescendingOrderReport;
